import React, {useState} from 'react'
import {useNavigation} from 'react-navi'
import {useTranslation} from 'react-i18next'
import {
  handleFormNumber,
  handleTaskTempValues,
  reverseConvertUnit,
  tempUnitStr,
  useTaskForm,
  useTaskGroupSelect,
  useMeasurementMethodSelect,
  valueExists,
  useRestaurantListField
} from '../../../config/utils'
import {useAppState} from '../../../state'
import {EquipmentTemperatureTaskResponse} from '../../../state/rest'
import {Button, ButtonRowWrap, ButtonRowGrid, IconButton} from '../../Atoms/Buttons'
import {BigLabelInput, LabelInput, Select, Toggler} from '../../Atoms/Forms'
import {Baseline, Grid, InvisibleContainer, Row, GridSpan} from '../../Layout/Grid'
import {MainLayoutWithoutStretch, RightSidebar, MainContent} from '../../Layout/Layout'
import LocationsList from '../../Organisms/Locations/LocationsList'
import TaskScheduling from '../../Organisms/Scheduler/TaskScheduling'
import {P} from '../../Atoms/Typography'
import {HeaderRow, HeaderActions} from '../../Molecules/ViewComponents/ViewHeader'
import TaskMenuModal from './TaskMenuModal'
import {CommonInformationFields, TasksGoBackHeaderTitle} from './CommonTasksComponents'
import IsUserHealthInspector from '../../../hooks/userIsHealthInspector'
import {IconAddItem, IconRemoveItem} from '../../../Assets/Icons/LargeIcons'
import styled from 'styled-components'
import {DefaultTagsWidget} from '../../../Components/Organisms/Tags/DefaultSuggestionWidget'
import CreateDishwasherTemperatureTask from './CreateDishwasherTemperatureTask'

interface FormProps {
  savedData?: EquipmentTemperatureTaskResponse
  applianceType?: string
  backPath?: string
}

interface EquipmentTaskRowFormProps {
  row: TaskRow
  index: number
  canCreateMultiple: boolean
  totalRows: number
  unit: string
  updateTaskRow: (id: string, field: keyof TaskRow, value: any) => void
  removeTaskRow: (id: string) => void
  updateDefaultSuggestions: (id: string, items: any[]) => void
}

type TaskRow = {
  id: string
  name: string
  equipmentName: string
  isAlarms: boolean
  minValue?: string
  maxValue?: string
  defaultSuggestions?: string[]
}

const TASK_KEY = 'equipmentTemperature'

const EquipmentTaskRowForm = React.memo(
  ({
    row,
    index,
    canCreateMultiple,
    totalRows,
    unit,
    updateTaskRow,
    removeTaskRow,
    updateDefaultSuggestions
  }: EquipmentTaskRowFormProps) => {
    const {t} = useTranslation('tasks')

    const hasInvalidLimits =
      valueExists(row.maxValue) && valueExists(row.minValue) && parseFloat(row.minValue!) >= parseFloat(row.maxValue!)

    // Stable reference — only recreated if row.id changes
    const handleDefaultSuggestionsChange = React.useCallback(
      (items: any[]) => updateDefaultSuggestions(row.id, items),
      [row.id, updateDefaultSuggestions]
    )

    return (
      <Baseline>
        <TaskUniqueFieldsRow>
          <BigLabelInput
            required
            placeholder={t('tasks:placeholders.newTaskName', 'New task name')}
            name={`name-${row.id}`}
            id={`name-${row.id}`}
            labelText={t('tasks:labels.taskName', 'Task name')}
            value={row.name}
            onChange={e => updateTaskRow(row.id, 'name', e.target.value)}
          />
          <Toggler
            label={t('tasks:labels.alarms', 'Alarms')}
            values={[t('common:togglerValues.off', 'Off'), t('common:togglerValues.on', 'On')]}
            initiallyChecked={row.isAlarms}
            name={`isAlarms-${row.id}`}
            onClick={() => updateTaskRow(row.id, 'isAlarms', !row.isAlarms)}
          />
          {row.isAlarms ? (
            <>
              <LabelInput
                required
                type="number"
                name={`minValue-${row.id}`}
                id={`minValue-${row.id}`}
                value={row.minValue ?? ''}
                onChange={e => updateTaskRow(row.id, 'minValue', e.target.value)}
                errorMsg={
                  hasInvalidLimits
                    ? (t('common:validation.valueTooHigh', 'Value must be lower than max value') as string)
                    : undefined
                }
                labelText={`${t('common:general.alarmMinLimit', 'Alarm min limit')} (${unit})`}
              />
              <LabelInput
                required
                type="number"
                name={`maxValue-${row.id}`}
                id={`maxValue-${row.id}`}
                value={row.maxValue ?? ''}
                onChange={e => updateTaskRow(row.id, 'maxValue', e.target.value)}
                errorMsg={
                  hasInvalidLimits
                    ? (t('common:validation.valueTooLow', 'Value must be higher than min value') as string)
                    : undefined
                }
                labelText={`${t('common:general.alarmMaxLimit', 'Alarm max limit')} (${unit})`}
              />
            </>
          ) : (
            <>
              <div />
              <div />
            </>
          )}
          {canCreateMultiple && totalRows > 1 ? (
            <TaskRowAction>
              <IconButton
                icon={<IconRemoveItem width="3rem" height="3rem" />}
                buttonProps={{onClick: () => removeTaskRow(row.id)}}
              />
            </TaskRowAction>
          ) : (
            <div />
          )}
        </TaskUniqueFieldsRow>

        <DefaultTagsWidget
          onChange={handleDefaultSuggestionsChange}
          savedItems={row.defaultSuggestions}
          title={t(
            'tasks:labels.defaultApplianceName',
            'Define default appliance names for what user is measuring. These names are shown when completing the task.'
          )}
        />

        {index < totalRows - 1 && <TaskRowSeparator />}
      </Baseline>
    )
  }
)

const CreateEquipmentTemperatureTaskForm = ({savedData, backPath, applianceType}: FormProps) => {
  const {t} = useTranslation('tasks')
  const {state} = useAppState()
  const {isHealthInspector} = IsUserHealthInspector()

  const isEditing = !!savedData?.id
  const isDuplicate = !!savedData && savedData.id == null
  const canCreateMultiple = !isEditing

  // helper functions
  const createEmptyRow = (): TaskRow => ({
    id: `${Date.now()}-${Math.random()}`,
    name: '',
    equipmentName: '',
    isAlarms: false,
    minValue: undefined,
    maxValue: undefined,
    defaultSuggestions: []
  })

  const rowFromSaved = (saved: any): TaskRow => ({
    id: `${Date.now()}-${Math.random()}`,
    name: saved.name ?? '',
    equipmentName: saved.equipmentName ?? '',
    isAlarms: valueExists(saved.minValue) && valueExists(saved.maxValue),
    minValue: saved.minValue?.toString(),
    maxValue: saved.maxValue?.toString(),
    defaultSuggestions: saved.defaultSuggestions ?? []
  })

  const addTaskRow = () => {
    setTaskRows(prev => [...prev, createEmptyRow()])
  }

  const removeTaskRow = (id: string) => {
    setTaskRows(prev => prev.filter(row => row.id !== id))
  }

  const updateTaskRow = (id: string, field: keyof TaskRow, value: any) => {
    setTaskRows(prev => prev.map(row => (row.id === id ? {...row, [field]: value} : row)))
  }

  const saved = savedData
    ? handleTaskTempValues(
        isDuplicate && savedData.name
          ? {
              ...savedData,
              name: `${t('tasks:labels.copyOf', 'Copy of')} ${savedData.name.toLowerCase()}`,
              sites: [state.site!]
            }
          : savedData,
        state.site!
      )
    : undefined

  const [taskRows, setTaskRows] = useState<TaskRow[]>([saved ? rowFromSaved(saved) : createEmptyRow()])
  // task form controller hook
  const fc = useTaskForm(TASK_KEY, saved)
  const rc = useRestaurantListField(
    {watch: fc.watch, setValue: fc.setValue},
    isDuplicate ? undefined : saved ? saved.sites : undefined
  )
  const {taskGroupOptions, selectTaskGroup, selectedTaskGroup} = useTaskGroupSelect(
    TASK_KEY,
    saved ? saved.taskGroup : undefined
  )

  const {selectMethod, selectedMeasurementMethod, measurementOptions} = useMeasurementMethodSelect(
    'device',
    saved ? saved.measurementMethod : undefined
  )

  const commonPayload = {
    taskGroup: selectedTaskGroup,
    measurementMethod: selectedMeasurementMethod,
    unit: state.site!.temperatureUnit,
    sites: rc.list,
    assets: fc.assets,
    lateByDays: fc.lateByDays
  }

  const taskPayloads = taskRows.map(row => ({
    ...commonPayload,
    name: row.name,
    equipmentName: row.equipmentName,
    defaultSuggestions: row.defaultSuggestions,
    minValue: row.isAlarms ? reverseConvertUnit(state.site!, handleFormNumber(row.minValue)) : undefined,
    maxValue: row.isAlarms ? reverseConvertUnit(state.site!, handleFormNumber(row.maxValue)) : undefined,
    alarmsEnabled: row.isAlarms
  }))

  const updateTaskRowDefaultSuggestions = React.useCallback((id: string, items: any[]) => {
    setTaskRows(prev => prev.map(row => (row.id === id ? {...row, defaultSuggestions: items} : row)))
  }, [])

  const isTaskRowValid = (row: TaskRow) => {
    if (!row.name) return false

    if (!row.isAlarms) return true

    return (
      valueExists(row.minValue) &&
      valueExists(row.maxValue) &&
      parseFloat(row.minValue!.toString()) < parseFloat(row.maxValue!.toString())
    )
  }

  const isFormValid = () => {
    return taskRows.every(isTaskRowValid)
  }

  return (
    <MainLayoutWithoutStretch>
      <MainContent variant="white">
        <HeaderRow
          modal={
            isEditing && (
              <TaskMenuModal
                isOpen={fc.modalOpen}
                onClose={() => fc.setModalOpen(false)}
                onDelete={fc.editingDisabled ? undefined : fc.remove}
                onDuplicate={fc.duplicate}
              />
            )
          }
        >
          <TasksGoBackHeaderTitle
            label={
              applianceType === 'coldRoom'
                ? isEditing
                  ? t('tasks:labels.editTask.coldRoomTemperature', 'Edit cold room temperature task')
                  : t('tasks:labels.newTask.coldRoomTemperature', 'Add cold room temperature task')
                : isEditing
                ? t('tasks:labels.editTask.equipmentTemperature', 'Edit appliance temperature task')
                : t('tasks:labels.newTask.equipmentTemperature', 'Add appliance temperature task')
            }
            isEditing={isEditing}
            backPath={backPath}
            backLabel={backPath ? t('tasks:labels.selectApplianceType', 'Select appliance type') : undefined}
          />
          {isEditing && <HeaderActions onMenu={() => fc.setModalOpen(true)} />}
        </HeaderRow>
        <InvisibleContainer>
          <Baseline>
            <Baseline>
              {taskRows.map((row, index) => (
                <EquipmentTaskRowForm
                  key={row.id}
                  row={row}
                  index={index}
                  totalRows={taskRows.length}
                  canCreateMultiple={canCreateMultiple}
                  unit={tempUnitStr(state.site!.temperatureUnit)}
                  updateTaskRow={updateTaskRow}
                  removeTaskRow={removeTaskRow}
                  updateDefaultSuggestions={updateTaskRowDefaultSuggestions}
                />
              ))}

              {canCreateMultiple && (
                <IconButton
                  icon={<IconAddItem width="3rem" height="3rem" />}
                  buttonProps={{
                    onClick: addTaskRow
                  }}
                />
              )}
            </Baseline>
            <Grid>
              <Baseline>
                <Select
                  returnId
                  nativeProps={{
                    name: 'taskGroup',
                    onChange(e) {
                      selectTaskGroup(e.target.value)
                    },
                    value: selectedTaskGroup.id
                  }}
                  id="taskGroup"
                  label={t('tasks:labels.taskGroup', 'Task group')}
                  options={taskGroupOptions}
                />

                <Select
                  returnId
                  nativeProps={{
                    name: 'measurementMethod',
                    onChange(e) {
                      selectMethod(measurementOptions.find(mo => mo.id === e.target.value)!.id)
                    },
                    value: selectedMeasurementMethod.id
                  }}
                  id="measurementMethod"
                  label={t('tasks:labels.measurementMethod', 'Measurement method')}
                  options={measurementOptions}
                />

                <TaskScheduling
                  enabled={fc.schedulingEnabled}
                  frequency={fc.selectedFrequency}
                  incomplete={fc.scheduleIncomplete}
                  onToggle={fc.toggleShow}
                  onFrequencyChange={fc.setSelectedFrequency}
                />
              </Baseline>
              <Baseline>
                <CommonInformationFields useForm={fc} />
              </Baseline>
            </Grid>
            <ButtonRowWrap>
              <div className="empty"></div>
              <ButtonRowGrid>
                <Button name="cancel-button" negative variant="secondary" disabled={fc.loading} onClick={fc.cancel}>
                  {t('common:buttons.cancel', 'Cancel')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={
                    fc.scheduleIncomplete ||
                    !isFormValid() ||
                    fc.loading ||
                    rc.list.length === 0 ||
                    fc.editingDisabled ||
                    isHealthInspector
                  }
                  onClick={() =>
                    isEditing
                      ? fc.update({
                          ...commonPayload,
                          ...taskPayloads[0]
                        })
                      : fc.submitBulkEquipmentTemperature(taskPayloads, commonPayload)
                  }
                >
                  {t('common:buttons.save', 'Save')}
                </Button>

                {fc.editingDisabled && (
                  <GridSpan span={2}>
                    <P>
                      {t(
                        'tasks:messages.taskInUseMessage',
                        'This task is in use for locations you don’t have access to. You can not edit or delete it but you can duplicate the task to create a new one'
                      )}
                    </P>
                  </GridSpan>
                )}
              </ButtonRowGrid>
            </ButtonRowWrap>
          </Baseline>
        </InvisibleContainer>
      </MainContent>
      <RightSidebar>
        <LocationsList
          controller={rc}
          cyKey={'task'}
          label={t('tasks:labels.selectLocation', 'Select which locations use this task')}
        />
      </RightSidebar>
    </MainLayoutWithoutStretch>
  )
}

const CreateEquipmentTemperatureTask = ({savedData, applianceType}: FormProps) => {
  const {t} = useTranslation('tasks')
  const nav = useNavigation()
  const selectedType =
    savedData?.taskType?.id === 'dishwasherTemperature'
      ? 'dishwasher'
      : applianceType ||
        (savedData?.taskType?.id === 'equipmentTemperature' || (savedData && savedData.id == null)
          ? 'coldRoom'
          : undefined)

  if (!savedData?.id && !selectedType) {
    return (
      <MainLayoutWithoutStretch>
        <MainContent variant="white">
          <HeaderRow>
            <TasksGoBackHeaderTitle
              label={t('tasks:labels.newTask.equipmentTemperature', 'Add appliance temperature task')}
              isEditing={false}
            />
          </HeaderRow>
          <ApplianceTypeChooser>
            <strong>{t('tasks:labels.selectApplianceType', 'Select which appliance type you want to add.')}</strong>
            <ApplianceTypeOption>
              <input
                type="radio"
                name="applianceType"
                value="coldRoom"
                onChange={() => nav.navigate('/create/task/equipmentTemperature/coldRoom')}
              />
              <span>{t('tasks:labels.coldRoom', 'Cold room')}</span>
            </ApplianceTypeOption>
            <ApplianceTypeOption>
              <input
                type="radio"
                name="applianceType"
                value="dishwasher"
                onChange={() => nav.navigate('/create/task/equipmentTemperature/dishwasher')}
              />
              <span>{t('tasks:labels.dishwasherType', 'Dishwasher')}</span>
            </ApplianceTypeOption>
          </ApplianceTypeChooser>
        </MainContent>
      </MainLayoutWithoutStretch>
    )
  }

  if (selectedType === 'dishwasher') {
    return (
      <CreateDishwasherTemperatureTask
        savedData={savedData as any}
        backPath={savedData?.id ? undefined : '/create/task/equipmentTemperature'}
      />
    )
  }

  return (
    <CreateEquipmentTemperatureTaskForm
      savedData={savedData}
      applianceType={selectedType}
      backPath={savedData?.id ? undefined : '/create/task/equipmentTemperature'}
    />
  )
}

export default CreateEquipmentTemperatureTask

const TaskRowSeparator = styled.hr`
  width: 100%;
  margin: 1.5rem 0;
  border: 0;
  border-top: 1px solid #e0e0e0;
`

const TaskUniqueFieldsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 140px 130px 130px 60px;
  gap: 1.5rem;
  align-items: end;
  width: 100%;
`

const TaskRowAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 0.25rem;
`

const ApplianceTypeChooser = styled.section`
  border: 1px solid #e0e3e8;
  padding: 1.5rem;
  margin: 1rem 0;
  display: grid;
  gap: 1rem;
`

const ApplianceTypeOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;

  input {
    width: 1.25rem;
    height: 1.25rem;
    margin: 0;
  }
`
